import { useTranslation } from '@mint-lib/i18n';
import { AddNoCircle } from '@mint-lib/icons';
import { Button } from '@mint-lib/tw-components';

import { EmptyState } from '../components/atoms/EmptyState.jsx';
import { LimitProgressBar } from '../components/atoms/LimitProgressBar.jsx';
import { Tooltip } from '../components/atoms/Tooltip.jsx';
import CustomMetricsTable from '../components/molecules/CustomMetricsTable.jsx';
import { PageHeader } from '../components/molecules/PageHeader.jsx';
import { RouterSearchBar } from '../components/molecules/RouterSearchBar.jsx';
import { useCustomMetricsDrawer } from '../contexts/CustomMetricsContext.jsx';
import { useCustomMetricsRouteApi } from '../hooks/useImportHubRouteApi.js';
import { useIsReadOnlyUser } from '../hooks/useIsReadOnlyUser.js';
import { useCustomMetrics } from '../queries/index.js';

export function CustomMetrics() {
  const { t } = useTranslation('@myn/import-hub');
  const searchParams = useCustomMetricsRouteApi().useSearch();

  const { data: customMetricsMetaData } = useCustomMetrics();
  const {
    totalItems: metricsTotalCount,
    additional: { maxCount: metricsMaxCount, canBeCreated: canMetricBeCreated },
  } = customMetricsMetaData;

  const { data, isLoading } = useCustomMetrics(searchParams, {
    throwOnError: true,
  });

  const isReadOnlyUser = useIsReadOnlyUser();

  const { openMetricForCreate, openMetricForUpdate, openMetricForDelete } =
    useCustomMetricsDrawer();

  const isEmptyMetricsArray = !isLoading && !data.totalItems;

  if (isEmptyMetricsArray && !searchParams.search) {
    return (
      <EmptyState
        icon="IllustrationEmptyState"
        text={t('There are no custom metrics yet')}
        description={t(
          'Click on the button below to start adding your custom metrics',
        )}
      >
        <Button
          onClick={openMetricForCreate}
          variant="contained"
          color="primary"
          className="mt-4"
          data-dd-action-name="Add custom metric"
        >
          <AddNoCircle className="size-4" />
          {t('Add custom metric')}
        </Button>
      </EmptyState>
    );
  }

  return (
    <>
      <PageHeader
        title={t('Custom Metrics')}
        description={t(
          'Create and manage customized metrics for mapping your data.',
        )}
        backLink={true}
      />
      {metricsTotalCount > 0 && (
        <div className="my-4 flex justify-between">
          <div className="flex items-center gap-4">
            <Tooltip
              description={t(
                'You have reached the maximum number of custom metrics',
              )}
              disabled={canMetricBeCreated ?? isReadOnlyUser}
              asChild
            >
              <Button
                variant="contained"
                color="primary"
                onClick={openMetricForCreate}
                disabled={!canMetricBeCreated || isReadOnlyUser}
                data-dd-action-name="Add New Custom Metric"
              >
                <AddNoCircle className="size-4" />
                {t('Add New Custom Metric')}
              </Button>
            </Tooltip>
            {metricsMaxCount ? (
              <LimitProgressBar
                value={metricsTotalCount}
                total={metricsMaxCount}
                tooltip={t(
                  'The number of custom metrics that are currently available for addition',
                )}
              />
            ) : null}
          </div>
          <RouterSearchBar className="w-[280px]" />
        </div>
      )}
      {isEmptyMetricsArray ? (
        <EmptyState
          icon="IllustrationNoResults"
          text={t('No results found for "{{searchValue}}"', {
            searchValue: searchParams.search,
          })}
          description={t(
            'Try adjusting your search in order to find what you are looking for.',
          )}
        />
      ) : (
        <CustomMetricsTable
          data={data}
          isLoading={isLoading}
          onMetricEdit={openMetricForUpdate}
          onMetricDelete={openMetricForDelete}
        />
      )}
    </>
  );
}
